import { Paper } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import ReactJson from 'react-json-view';

import {
  getMonitor,
  getNotificationHistoryForAlert,
} from '~/src/dash/api/monitors/monitors_methods';
import {
  EMonitorNotificationProvider,
  EMonitorTriggerType,
  IMonitor,
  IMonitorNotificationHistory,
} from '~/src/dash/api/monitors/monitors_models';
import {
  MonitorTriggerTypeToString,
  providerToString,
} from '~/src/dash/api/monitors/monitors_utils';
import { DEMO_ACCOUNTS } from '~/src/dash/globals/constants';
import { IStore } from '~/src/dash/reducers/store';
import { ISessionStateProps, IUserStateProps, IWithStylesProps } from '~/src/dash/types/props';
import {
  ChipDelivering,
  ChipInactive,
  ChipPending,
  ChipUntargeted,
} from '~/src/dash/utils/campaign-status';
import { decorate } from '~/src/dash/utils/decorator';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';

export interface IProps extends ISessionStateProps, IWithStylesProps, IUserStateProps {
  monitor: IMonitor;
  alertID?: number;
}

function triggerTypeToChip(m: EMonitorTriggerType): any {
  switch (m) {
    case EMonitorTriggerType.MonitorTriggerType_ALERT:
      return ChipUntargeted;
    case EMonitorTriggerType.MonitorTriggerType_WARNING:
      return ChipPending;
    case EMonitorTriggerType.MonitorTriggerType_ALERT_RECOVERY:
      return ChipDelivering;
    case EMonitorTriggerType.MonitorTriggerType_WARNING_RECOVERY:
      return ChipDelivering;
    case EMonitorTriggerType.MonitorTriggerType_SHUTDOWN:
      return ChipUntargeted;
  }
  return ChipDelivering;
}

function MonitorNotificationsHistoryComp(props: IProps) {
  const [notifications, setNotifications] = useState(
    undefined as any as IMonitorNotificationHistory[],
  );
  const [updating, setUpdating] = useState(false);

  const refreshMonitor = function () {
    // using monitor OR alert
    setUpdating(true);

    if (props.alertID !== undefined) {
      getNotificationHistoryForAlert(
        props.monitor.ID,
        props.alertID,
        props.session.publisher.ID,
      ).then(([notifications, err]) => {
        setUpdating(false);
        if (err !== undefined) {
          console.error(err);
        } else if (notifications !== undefined) {
          setNotifications(notifications);
        }
      });
    } else {
      getMonitor(props.monitor.ID.toString(), props.session.publisher.ID).then(([monitor, err]) => {
        setUpdating(false);
        if (err !== undefined) {
          console.error(err);
        } else if (monitor !== undefined) {
          setNotifications(monitor.NotificationsHistory);
        }
      });
    }
  };

  useEffect(() => {
    refreshMonitor();
  }, []);

  const renderSingleEvent = function (event: IMonitorNotificationHistory, username: string) {
    const Chip = triggerTypeToChip(event.TriggeredType);
    const providerName = providerToString(event.Provider);
    let sendDate;
    if (event.CreatedAt !== undefined) {
      sendDate = new Date(event.CreatedAt).toLocaleString();
    }

    return (
      <Paper classes={{ root: props.classes.paper }} style={{ backgroundColor: '#f9f9f9' }}>
        <Chip label={MonitorTriggerTypeToString(event.TriggeredType)} />
        {event.Provider === EMonitorNotificationProvider.MonitorNotificationProvider_OPSGENIE &&
          hasRoleSuperUser(props.user.user) && (
            <ChipInactive label={'*Super User Only'} style={{ marginLeft: '4px' }} />
          )}
        <p>Type: {providerName}</p>
        {sendDate && <p>Triggered {sendDate}</p>}
        {!DEMO_ACCOUNTS.includes(username) && (
          <ReactJson displayDataTypes={false} src={event.SentData || {}} />
        )}
      </Paper>
    );
  };

  let monitorNotifications;
  // while updating, use history from props.monitor object
  if (updating && props.monitor !== undefined) {
    monitorNotifications = props.monitor.NotificationsHistory;
  }
  // if done updating, use history from api response
  if (!updating && notifications !== undefined) {
    monitorNotifications = notifications;
  }

  monitorNotifications = (monitorNotifications || []).filter(
    (notification: IMonitorNotificationHistory) => notification.AlertID === props.alertID,
  );

  if (monitorNotifications === undefined || monitorNotifications.length === 0) {
    return <div style={{ paddingLeft: '30px' }}>0 notifications found</div>;
  }

  return (
    <div>
      {monitorNotifications.map((value) => {
        return renderSingleEvent(value, props.user.user.Username);
      })}
    </div>
  );
}

const style = (theme: any) => ({
  paper: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing.unit * 2,
    paddingBottom: theme.spacing.unit * 2,
    marginBottom: theme.spacing.unit * 4,
  },
  section: {
    display: 'block',
    marginBottom: theme.spacing.unit * 2,
  },
  button: {
    margin: theme.spacing.unit,
  },
  drawerPaper: {
    width: '100%',
  },
});

export const MonitorNotificationsHistory = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: style,
    router: true,
  },
  MonitorNotificationsHistoryComp,
);
